<template>
  <aside
    ref="panel"
    class="cap shrink-0 flex min-h-0 border-l border-gray-200 bg-white"
    :class="{ 'is-resizing': resizing }"
    :style="{ width: `${width}px` }"
    aria-label="Canvas agent"
  >
    <div
      class="cap-splitter"
      role="separator"
      aria-orientation="vertical"
      title="Drag to resize · double-click to reset"
      @pointerdown="startResize"
      @dblclick="resetWidth"
    />
    <AgentConversation
      ref="conversation"
      :entry="entry"
      :storage-key="storageKey"
      profile="canvas"
      compact
      @close="$emit('close')"
    />
  </aside>
</template>

<script>
import { z } from 'zod';
import AgentConversation from '@/components/Beta/AgentConversation.vue';
import '@/components/Beta/agent.css';
import { sessionEntry } from '@/services/agentSessionController';
import { BrowserStore } from '@/utils/BrowserStore';
import agentPageContext from '@/mixins/agentPageContext';

const layoutStore = new BrowserStore('canvas-agent-layout-v1', z.object({
  width: z.number().nullable().default(null),
}));
const DEFAULT_WIDTH = 400;
const PANEL_MIN = 340;
const CANVAS_MIN = 480;

export default {
  name: 'CanvasAgentPanel',
  components: { AgentConversation },
  mixins: [agentPageContext],
  props: {
    containerWidth: { type: Number, default: 0 },
  },
  emits: ['close', 'workflow-saved'],
  data() {
    return { savedWidth: layoutStore.load().width, resizing: false };
  },
  computed: {
    scope() {
      return `${this.$store.state.tenant.current_tenant_slug || ''}:${this.$store.state.auth.current_user?.id || ''}`;
    },
    entry() {
      return sessionEntry(`${this.scope}:canvas`);
    },
    storageKey() {
      return `agent-canvas:${this.scope}`;
    },
    maxWidth() {
      return this.containerWidth ? Math.max(PANEL_MIN, this.containerWidth - CANVAS_MIN) : Infinity;
    },
    width() {
      return Math.min(this.maxWidth, Math.max(PANEL_MIN, this.savedWidth || DEFAULT_WIDTH));
    },
    savedWorkflows() {
      return this.entry.state.artifacts
        .filter((a) => a.kind === 'data_workflow')
        .map((a) => a.id)
        .join(',');
    },
  },
  watch: {
    savedWorkflows(ids, before) {
      if (ids && ids !== before) this.$emit('workflow-saved');
    },
  },
  mounted() {
    this.entry.view = this;
  },
  beforeUnmount() {
    if (this.entry.view === this) this.entry.view = null;
  },
  methods: {
    startResize(e) {
      if (e.button !== 0) return;
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = this.width;
      this.resizing = true;
      const move = (ev) => {
        this.savedWidth = Math.round(Math.min(this.maxWidth, Math.max(PANEL_MIN, startWidth + startX - ev.clientX)));
      };
      const stop = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', stop);
        window.removeEventListener('pointercancel', stop);
        this.resizing = false;
        layoutStore.save({ width: this.savedWidth });
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', stop);
      window.addEventListener('pointercancel', stop);
    },
    resetWidth() {
      this.savedWidth = null;
      layoutStore.save({ width: null });
    },
  },
};
</script>

<style scoped>
.cap { position: relative; }
.cap :deep(.ae-card) { border: 0; border-radius: 0; flex: 1; }
.cap-splitter { position: absolute; top: 0; bottom: 0; left: -4px; width: 8px; z-index: 5; cursor: col-resize; touch-action: none; }
.cap-splitter::before { content: ''; position: absolute; top: 0; bottom: 0; left: 2px; width: 4px; border-radius: 2px; background: transparent; transition: background 0.15s; }
.cap-splitter:hover::before, .cap.is-resizing .cap-splitter::before { background: #cfcabf; }
.cap.is-resizing { user-select: none; }
</style>
